import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../../services/apiService';
import { trackBillingAction } from '../../utils/billingAnalytics';

const PREVIEW_TTL_MS = 5 * 60 * 1000;

export default function BillingModalSwitchConfirm({ summary, source, onSuccess, onBack, onStateConflict }) {
  const posthog = usePostHog();
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const retryRef = useRef(0);

  const currentInterval = summary?.plan?.interval;
  const targetInterval = currentInterval === 'monthly' ? 'annual' : 'monthly';
  const isUpgrade = currentInterval === 'monthly';

  useEffect(() => {
    loadPreview({ attempt: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadPreview({ attempt }) {
    setPreviewError(null);
    try {
      const targetPriceId = resolveTargetPriceId(summary, targetInterval);
      if (!targetPriceId) throw new Error('missing_target_price_id');

      const body = await apiService.invokeEdgeFunction('preview-plan-switch', {
        body: { targetPriceId },
      });
      setPreview({ ...body, targetPriceId });
    } catch (e) {
      if (e?.status >= 500 && attempt < 2) {
        retryRef.current = attempt + 1;
        setTimeout(() => loadPreview({ attempt: attempt + 1 }), 400 * (attempt + 1));
        return;
      }
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModalSwitchConfirm', action: 'preview_plan_switch',
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: summary?.status, attempt } },
      });
      setPreviewError(e?.code || e?.message || 'preview_failed');
    }
  }

  async function handleConfirm() {
    if (!preview || pendingConfirm) return;

    const isStale = Date.now() - new Date(preview.previewedAt).getTime() > PREVIEW_TTL_MS;
    if (isStale) {
      setPreview(null);
      loadPreview({ attempt: 0 });
      return;
    }

    setPendingConfirm(true);
    setConfirmError(null);
    const started = Date.now();
    trackBillingAction(posthog, 'billing_action_clicked', { action: 'switch_plan', status: summary.status, source });

    try {
      const idempotencyKey = crypto.randomUUID();
      const body = await apiService.invokeEdgeFunction('billing-action', {
        body: {
          action: 'switch_plan',
          targetPriceId: preview.targetPriceId,
          prorationBillingMode: isUpgrade ? 'prorated_immediately' : 'full_next_billing_period',
        },
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      trackBillingAction(posthog, 'billing_action_completed', {
        action: 'switch_plan', status: summary.status, source, duration_ms: Date.now() - started,
      });
      onSuccess?.(body);
    } catch (e) {
      if (e?.status === 409 && e?.code === 'state_conflict') {
        trackBillingAction(posthog, 'billing_action_failed', {
          action: 'switch_plan', status: summary.status, source, duration_ms: Date.now() - started, error_code: 'state_conflict',
        });
        onStateConflict?.({
          action: 'switch_plan',
          args: { targetPriceId: preview.targetPriceId, prorationBillingMode: isUpgrade ? 'prorated_immediately' : 'do_not_bill' },
          previewedAt: preview.previewedAt,
        });
        return;
      }
      trackBillingAction(posthog, 'billing_action_failed', {
        action: 'switch_plan', status: summary.status, source, duration_ms: Date.now() - started, error_code: e?.code ?? e?.message,
      });
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModalSwitchConfirm', action: 'switch_plan',
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: summary?.status, targetPriceId: preview?.targetPriceId } },
      });
      setConfirmError(e?.code || e?.message || 'switch_failed');
    } finally {
      setPendingConfirm(false);
    }
  }

  if (previewError && !preview) {
    return (
      <View style={styles.root}>
        <Text role="alert" style={styles.error}>We could not load the preview. Please try again.</Text>
        <View style={styles.btnRow}>
          <Pressable onPress={onBack} style={({ pressed }) => [styles.btn, styles.secondary, pressed && { opacity: 0.7 }]}>
            <Text style={[styles.btnText, styles.secondaryText]}>Back</Text>
          </Pressable>
          <Pressable onPress={() => loadPreview({ attempt: 0 })} style={({ pressed }) => [styles.btn, styles.primary, pressed && { opacity: 0.8 }]}>
            <Text style={[styles.btnText, styles.primaryText]}>Retry</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (!preview) {
    return (
      <View style={styles.root}>
        <Text style={styles.body}>Loading preview...</Text>
      </View>
    );
  }

  const zeroProration = preview.prorationChargeToday?.amountRaw === 0;

  return (
    <View style={styles.root}>
      <Text style={styles.title}>
        {isUpgrade ? 'Switch to annual' : 'Switch to monthly'}
      </Text>
      {isUpgrade ? (
        <Text style={styles.body}>
          You will be charged {preview.prorationChargeToday?.amountFormatted} today for the remainder of your current period, then {preview.nextRenewalAmount?.amountFormatted} on {formatDate(preview.nextRenewalDate)}.
        </Text>
      ) : (
        <Text style={styles.body}>
          Your subscription stays annual until {formatDate(preview.nextRenewalDate)}. On that date it switches to monthly and you will be charged {preview.nextRenewalAmount?.amountFormatted || ''}. No charge today.
        </Text>
      )}
      {zeroProration && isUpgrade && (
        <Text style={styles.zeroNote}>No proration charge today.</Text>
      )}

      {confirmError && <Text role="alert" style={styles.error}>Switch did not complete. Please try again.</Text>}

      <View style={styles.btnRow}>
        <Pressable onPress={onBack} style={({ pressed }) => [styles.btn, styles.secondary, pressed && { opacity: 0.7 }]}>
          <Text style={[styles.btnText, styles.secondaryText]}>Back</Text>
        </Pressable>
        <Pressable
          onPress={handleConfirm}
          disabled={pendingConfirm}
          style={({ pressed }) => [styles.btn, styles.primary, pressed && { opacity: 0.8 }]}
        >
          <Text style={[styles.btnText, styles.primaryText]}>
            {pendingConfirm ? 'Switching...' : 'Confirm switch'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

// The summary carries the CURRENT plan's priceId but not necessarily the
// alternate. For the initial launch we rely on the backend to validate
// the target price ID against the known-set; the frontend computes the
// target by toggling interval and relying on a matching priceId via the
// paywall config.
function resolveTargetPriceId(summary, targetInterval) {
  // Summary may include `alternatePlanPrice` but not the price_id; for now
  // we fall back to the environment config.
  try {
    // Expo environment exposes EXPO_PUBLIC_* vars at build time.
    // eslint-disable-next-line no-undef
    return targetInterval === 'monthly'
      ? process.env.EXPO_PUBLIC_PADDLE_PRO_PRICE_ID
      : process.env.EXPO_PUBLIC_PADDLE_PRO_ANNUAL_PRICE_ID;
  } catch {
    return null;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 18, gap: 12 },
  title: { fontSize: 17, fontWeight: '600', color: '#FFFFFF' },
  body: { fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 20 },
  zeroNote: { fontSize: 13, color: '#3AE869' },
  btnRow: { flexDirection: 'row', gap: 10, marginTop: 6 },
  btn: { flex: 1, paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  secondary: { backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)' },
  primary: { backgroundColor: '#0556F7' },
  btnText: { fontSize: 14, fontWeight: '600' },
  primaryText: { color: '#FFFFFF' },
  secondaryText: { color: '#FFFFFF' },
  error: { fontSize: 13, color: '#FF6B6B' },
});
